import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import {
  clearAuthStorage,
  getStoredRefreshToken,
  getStoredToken,
  isAuthEndpoint,
  persistTokens,
  resolveAuthKind,
  type AuthKind,
} from '@/utils/tokenSession';
import type { ApiResponse, AuthTokens } from '@/types/auth';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean; _authKind?: AuthKind };

class ApiClient {
  private client: AxiosInstance;
  private refreshPromises: Partial<Record<AuthKind, Promise<string>>> = {};

  constructor() {
    this.client = axios.create({
      baseURL: env.apiBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const kind = resolveAuthKind(config.url);
      (config as RetriableConfig)._authKind = kind;
      const token = getStoredToken(kind);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response.data,
      async (error: AxiosError) => {
        const original = error.config as RetriableConfig | undefined;
        const kind = original?._authKind ?? resolveAuthKind(original?.url);

        if (
          error.response?.status === 401 &&
          original &&
          !original._retry &&
          !isAuthEndpoint(original.url) &&
          getStoredRefreshToken(kind)
        ) {
          original._retry = true;
          try {
            const newToken = await this.refreshAccessToken(kind);
            original.headers.Authorization = `Bearer ${newToken}`;
            return this.client(original);
          } catch {
            clearAuthStorage(kind);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async refreshAccessToken(kind: AuthKind): Promise<string> {
    if (this.refreshPromises[kind]) {
      return this.refreshPromises[kind]!;
    }

    const refreshToken = getStoredRefreshToken(kind);
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const path = kind === 'admin' ? '/auth/admins/refresh' : '/auth/users/refresh';

    this.refreshPromises[kind] = axios
      .post<ApiResponse<{ tokens: AuthTokens }>>(`${env.apiBaseUrl}${path}`, { refreshToken })
      .then((res) => {
        const tokens = res.data.data?.tokens;
        if (!tokens) throw new Error('Refresh failed');
        persistTokens(kind, tokens.accessToken, tokens.refreshToken);
        return tokens.accessToken;
      })
      .finally(() => {
        delete this.refreshPromises[kind];
      });

    return this.refreshPromises[kind]!;
  }

  get<T>(url: string, config?: { params?: Record<string, string | number> }) {
    return this.client.get<T, T>(url, config);
  }

  post<T>(url: string, data?: unknown, config?: { timeout?: number; headers?: Record<string, string> }) {
    return this.client.post<T, T>(url, data, config);
  }

  patch<T>(url: string, data?: unknown) {
    return this.client.patch<T, T>(url, data);
  }

  put<T>(url: string, data?: unknown) {
    return this.client.put<T, T>(url, data);
  }
}

export const apiClient = new ApiClient();

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return 'Unable to reach the server. Check your connection and try again.';
    }

    const data = error.response.data as
      | { message?: string; errors?: Record<string, string[] | string> }
      | undefined;

    if (data?.message?.trim()) {
      return data.message.trim();
    }

    if (data?.errors) {
      const first = Object.values(data.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .find((value) => typeof value === 'string' && value.trim());
      if (first) return first.trim();
    }

    if (error.response.status === 401) {
      return fallback;
    }

    return error.message || fallback;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}
